import { ErrorEnum } from 'js/src/client';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import type { AppDispatch } from 'js/src/libs/RootState';
import { isApiErrorWithProperty } from 'js/src/types';

// Matches the backend's VIDEO_LIMIT_FEATURE_NAME (web/video_limit.py). Feature-usage-limit 403s
// carry a `feature_name`; we scope to this one so other limits (e.g. AI voiceover credits) that
// share the `hit-feature-usage-limit` error_code don't trigger the video-limit upgrade modal.
const VIDEO_LIMIT_FEATURE_NAME = 'video_limit';

/**
 * True when `err` is the backend's "free-plan video limit reached" rejection (a 403 with the
 * feature-usage-limit payload for the video-count limit). The frontend's proactive
 * {@link useVideoLimitGate} check can be stale (another tab, or videos created earlier this
 * session), so the backend 403 is the real source of truth — use this to recognize it.
 */
export function isVideoLimitError(err: unknown): boolean {
    return (
        isApiErrorWithProperty(err, 'error_code') &&
        err.body.error_code === ErrorEnum.HIT_FEATURE_USAGE_LIMIT &&
        err.body.feature_name === VIDEO_LIMIT_FEATURE_NAME
    );
}

/**
 * If `err` is the video-limit rejection, show the upgrade modal and return `true`. Otherwise return
 * `false` so the caller can fall back to its normal error handling. Use this in the `catch` of any
 * video-creating request so a stale client surfaces the upsell instead of a generic error.
 */
export function handleVideoLimitError(err: unknown, dispatch: AppDispatch): boolean {
    if (!isVideoLimitError(err)) {
        return false;
    }
    dispatch(
        showModal({
            modal_id: MODAL_IDS.FEATURE_LOCK,
            modal_context: { featureName: LOCKED_FEATURES.VIDEO_LIMIT },
        }),
    );
    return true;
}

// Matches the backend's LimitedFeatureName.TRANSLATION (web/enums.py). The translation endpoints
// reject with a feature-usage-limit 403 once the team is out of its per-plan translation allowance.
const TRANSLATION_FEATURE_NAME = 'translation';

/**
 * True when `err` is the backend's "translation quota reached" rejection. The client gate
 * (`team_settings.can_translate_videos`) can be stale within a session, so the backend 403 is the
 * real source of truth.
 */
export function isTranslationLimitError(err: unknown): boolean {
    return (
        isApiErrorWithProperty(err, 'error_code') &&
        err.body.error_code === ErrorEnum.HIT_FEATURE_USAGE_LIMIT &&
        err.body.feature_name === TRANSLATION_FEATURE_NAME
    );
}

/**
 * If `err` is the translation-quota rejection, show the upgrade modal and return `true`. Otherwise
 * return `false` so the caller can fall back to its normal error handling.
 */
export function handleTranslationLimitError(err: unknown, dispatch: AppDispatch): boolean {
    if (!isTranslationLimitError(err)) {
        return false;
    }
    dispatch(
        showModal({
            modal_id: MODAL_IDS.FEATURE_LOCK,
            modal_context: { featureName: LOCKED_FEATURES.TRANSLATION },
        }),
    );
    return true;
}
